import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { Card } from 'react-bootstrap';

const QuizResults = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        fetchResults();
    }, [id]);

    const fetchResults = async () => {
        try {
            const response = await api.get(`/api/quiz-attempts/${id}`);
            setResults(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch results');
            console.error('Error fetching results:', err);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="text-center mt-5">Loading...</div>;
    }

    if (error) {
        return <div className="alert alert-danger mt-5">{error}</div>;
    }

    if (!results) {
        return <div className="alert alert-danger mt-5">Results not found</div>;
    }

    const isPassed = results.attempt.score >= results.passing_score;
    const scorePercentage = (results.attempt.score / results.max_score) * 100;

    return (
        <div className="container mt-4">
            <Card>
                <Card.Header>
                    <h2>Quiz Results</h2>
                </Card.Header>
                <Card.Body>
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <h3>{results.quiz.title}</h3>
                            <div className="text-muted" dangerouslySetInnerHTML={{ __html: results.quiz.description }} />
                        </div>
                        <div className="col-md-6">
                            <div className="card">
                                <div className="card-body">
                                    <h4 className="card-title">Summary</h4>
                                    {user.role === 'admin' && (
                                        <div className="mb-3">
                                            <p className="mb-1">
                                                <strong>Username:</strong> {results.user.username}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Email:</strong> {results.user.email}
                                            </p>
                                            <p className="mb-1">
                                                <strong>Attempt Date:</strong> {new Date(results.attempt.completed_at).toLocaleString()}
                                            </p>
                                        </div>
                                    )}
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Score:</span>
                                        <span className="fw-bold">{results.attempt.score} / {results.max_score}</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Percentage:</span>
                                        <span className="fw-bold">{scorePercentage.toFixed(1)}%</span>
                                    </div>
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Passing Score:</span>
                                        <span className="fw-bold">{results.passing_score}</span>
                                    </div>
                                    <div className="d-flex justify-content-between">
                                        <span>Status:</span>
                                        <span className={`fw-bold ${isPassed ? 'text-success' : 'text-danger'}`}>
                                            {isPassed ? 'Passed' : 'Failed'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <h4 className="mb-3">Your Answers</h4>
                    {results.questions.map((question, index) => (
                        <div key={question.id} className="card mb-3">
                            <div className="card-body">
                                <h5 className="card-title">
                                    Question {question.question_number}
                                    <small className="text-muted ms-2">
                                        ({question.points} points)
                                    </small>
                                </h5>
                                <p className="card-text" dangerouslySetInnerHTML={{ __html: question.question_text }}></p>
                                <div className="answers">
                                    {question.answers.map(answer => (
                                        <div
                                            key={answer.id}
                                            className={`form-check mb-2 ${
                                                answer.id === question.correct_answer_id
                                                    ? 'text-success'
                                                    : answer.id === question.user_answer_id && answer.id !== question.correct_answer_id
                                                    ? 'text-danger'
                                                    : ''
                                            }`}
                                        >
                                            <input
                                                type="radio"
                                                className="form-check-input"
                                                checked={answer.id === question.user_answer_id}
                                                disabled
                                            />
                                            <label className="form-check-label">
                                                <span dangerouslySetInnerHTML={{ __html: answer.answer_text }}></span>
                                            </label>
                                        </div>
                                    ))}
                                </div>
                                {question.user_answer_id !== question.correct_answer_id && (
                                    <div className="alert alert-info mt-2">
                                        <small>
                                            <strong>Correct Answer:</strong>{' '}
                                            
                                            <span dangerouslySetInnerHTML={{ __html: question.answers.find(a => a.id === question.correct_answer_id)?.answer_text }}></span>
                                        </small>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}

                    <div className="d-flex justify-content-between mt-4">
                        <button
                            className="btn btn-secondary"
                            onClick={() => navigate('/quizzes')}
                        >
                            Back to Quizzes
                        </button>
                        {user.role === 'admin' && (
                            <button
                                className="btn btn-primary"
                                onClick={() => navigate(`/quiz-editor/${results.quiz.id}`)}
                            >
                                Edit Quiz
                            </button>
                        )}
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default QuizResults; 